import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './session'
import { useRoster } from './roster'
import { TAB_PATH, type View } from './views'
import { dropEmptyPlaceholder, withChunk, withView, type Turn } from './turns'
import { PEARL_KEY, fromStore, toStore, usableView } from './pearlstore'

/**
 * ONE conversation with Pearl, held above the routes.
 *
 * WHY IT MOVED OUT OF THE COMPONENT
 *
 * Two reasons, and the second one is why this file exists at all.
 *
 * **It was being thrown away.** The conversation was component state on a
 * routed screen, so the moment Renee left the Pearl tab it was gone — and
 * leaving the Pearl tab is precisely what she does when Pearl has just
 * explained how to do something. She would come back to a greeting, having lost
 * the instructions she went to follow.
 *
 * **There are now two places to talk to her.** The full Pearl tab, for sitting
 * down and asking things, and a side panel for keeping her open while working
 * through a screen. Those must be two views of ONE conversation. Two components
 * each holding their own would mean asking a question on the tab, opening the
 * panel, and finding Pearl remembers nothing — which reads as broken rather
 * than as two features.
 *
 * WHAT STAYED IN THE COMPONENT
 *
 * Only drawing. Everything that decides anything — what is kept, what is
 * restored, what is too old — is in `pearlstore.ts` and asserted in
 * `test-pearlstore.mjs`.
 *
 * THE SECURITY MODEL IS UNCHANGED and must stay that way: `/api/chat` is a
 * Cloudflare Function, the Anthropic key lives on the server, and the session
 * token goes up with every request so Pearl's roster lookups run under Renee's
 * own permissions. Nothing here may call `api.anthropic.com` directly — that
 * would put the key in a public bundle.
 */

export type PearlChat = {
  turns: Turn[]
  draft: string
  busy: boolean
  status: string | null
  error: string | null
  setDraft: (s: string) => void
  ask: (text: string) => Promise<void>
  /** Accept a view: set the roster, then go and look at it. */
  accept: (v: View) => void
  /** Start again. The stored conversation goes with it. */
  clear: () => void
  panelOpen: boolean
  openPanel: () => void
  closePanel: () => void
}

const Ctx = createContext<PearlChat | null>(null)

export function usePearl(): PearlChat {
  const c = useContext(Ctx)
  if (!c) throw new Error('usePearl must be used inside PearlProvider')
  return c
}

export function PearlProvider({ children }: { children: React.ReactNode }) {
  const { session } = useAuth()
  const roster = useRoster()
  const navigate = useNavigate()

  /**
   * Restored once, at mount, from whatever the last visit left behind.
   *
   * In a lazy initialiser rather than an effect so the first paint already has
   * it: restoring a moment later would show the greeting and then replace it,
   * which looks like the conversation arriving from somewhere rather than
   * having been there all along.
   */
  const [restored] = useState(() => {
    try {
      return fromStore(window.localStorage.getItem(PEARL_KEY), Date.now())
    } catch {
      // A profile that will not let us read is a profile with no history.
      return { turns: [], at: 0 }
    }
  })
  const [turns, setTurns] = useState<Turn[]>(restored.turns)
  /**
   * When this conversation BEGAN — carried across from storage, not restamped.
   *
   * Writing `Date.now()` on every save re-armed the twelve-hour expiry on every
   * page load, so it silently meant "twelve hours since the app was last open".
   * For anybody who opens the dashboard daily — which is the whole intended use
   * — a conversation would never have expired at all.
   *
   * Zero means "no conversation yet"; the first turn stamps it.
   */
  const startedAt = useRef(restored.at)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)

  /**
   * Written when a turn ARRIVES or a reply FINISHES — not on every chunk, and
   * not only when idle.
   *
   * The first version skipped the write whenever `busy` was true. Asking a
   * question sets the question and `busy` in the same commit, so the question
   * was not stored until the answer completed: close the tab mid-reply and both
   * halves were gone, with storage still holding the state from before she
   * asked. The question is the more valuable half — it is what lets her see
   * what she was in the middle of.
   *
   * The dependency is `turns.length`, deliberately, not `turns`. Content
   * changes on every streamed chunk and putting a whole conversation through
   * `JSON.stringify` and into disk storage dozens of times per reply would be
   * felt on the one screen that has to stay responsive. Length changes when a
   * turn is added; `busy` changes when the reply lands. Between those two the
   * effect closes over the current turns and writes the finished thing.
   */
  useEffect(() => {
    if (turns.length === 0) startedAt.current = 0
    else if (startedAt.current === 0) startedAt.current = Date.now()
    try {
      const payload = toStore(turns, startedAt.current || Date.now())
      if (payload === null) window.localStorage.removeItem(PEARL_KEY)
      else window.localStorage.setItem(PEARL_KEY, payload)
    } catch {
      // Full, or refused. The conversation still works for this visit, which is
      // a far better outcome than a thrown error from a storage write taking
      // the screen down.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns.length, busy])

  /**
   * `busy` as a ref as well as state, and the ref is the one that guards.
   *
   * The guard used to read the `busy` STATE captured in `ask`'s closure, which
   * only becomes true after a commit — so two calls dispatched in one tick both
   * passed it. The second built its outgoing list from the stale `turns`,
   * dropping the in-flight reply, and two streams then wrote into the same
   * conversation with whichever finished first clearing `busy` for both. The
   * disabled Send button makes it hard to reach; nothing made it impossible.
   *
   * (There was an `alive` ref here, copied from `SyncStrip`, set on mount and
   * never read. It implied an unmount guard that did not exist. Deleted rather
   * than wired up: this provider sits above the routes and does not unmount
   * while a stream is running.)
   */
  const busyRef = useRef(false)

  const ask = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || busyRef.current) return

      const token = session?.access_token
      if (!token) {
        setError('Your sign-in seems to have expired. Refresh the page and try again.')
        return
      }

      /**
       * Claimed HERE, after every early return, and immediately before the
       * `try` whose `finally` releases it.
       *
       * Claiming it at the top of the function meant the expired-session path
       * returned still holding it — and nothing else ever released it, so Pearl
       * would refuse to send anything for the rest of the session, silently,
       * with a working-looking Send button. A guard that can be leaked is worse
       * than the race it was added to close.
       *
       * Nothing between the check above and this line awaits, so no second call
       * can slip through the gap.
       */
      busyRef.current = true

      const outgoing: Turn[] = [...turns, { role: 'user', content: trimmed }]
      setTurns(outgoing)
      setDraft('')
      setError(null)
      setStatus(null)
      setBusy(true)

      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
          body: JSON.stringify({ messages: outgoing }),
        })

        if (!res.ok || !res.body) {
          const detail = await res.json().catch(() => null)
          setError((detail as { error?: string } | null)?.error ?? "Pearl couldn't answer that. Try again.")
          return
        }

        // An empty assistant turn to stream into.
        setTurns((t) => [...t, { role: 'assistant', content: '' }])

        await readStream(res.body, (event) => {
          if (event.type === 'text') {
            setStatus(null)
            setTurns((t) => withChunk(t, event.text))
          } else if (event.type === 'status') {
            setStatus(event.text)
          } else if (event.type === 'error') {
            setStatus(null)
            setError(event.text)
          } else if (event.type === 'view') {
            // Attached to the reply it belongs to, not shown on its own — the
            // button only makes sense underneath what Pearl said about it.
            setTurns((t) => withView(t, event.view))
          }
        })
      } catch {
        setError('Lost connection to Pearl. Try again.')
      } finally {
        // In a `finally`, and the ref before the state: an early return from the
        // failure path above used to clear `busy` on its own, which meant one
        // exit released it and the other did not.
        busyRef.current = false
        setStatus(null)
        setBusy(false)
        setTurns(dropEmptyPlaceholder)
      }
    },
    [session, turns],
  )

  /**
   * Accept a view. The roster is set before navigating, so the list is already
   * right when it appears rather than filtering itself in front of her.
   *
   * The panel closes on the way. She asked to be shown a group of agents; the
   * one thing she does not want is Pearl sitting over the top of them.
   */
  const accept = useCallback(
    (v: View) => {
      roster.applyView(v)
      setPanelOpen(false)
      navigate(TAB_PATH[v.tab] ?? '/')
    },
    [roster, navigate],
  )

  const clear = useCallback(() => {
    setTurns([])
    setError(null)
    setStatus(null)
    // The half-typed question goes too. "Start again" leaving her last sentence
    // sitting in the box is the one thing it does not mean.
    setDraft('')
    try {
      window.localStorage.removeItem(PEARL_KEY)
    } catch {
      // Nothing to remove, or not allowed to. Either way the screen is clear.
    }
  }, [])

  /**
   * Hoisted out of the memo, and stable for the life of the provider.
   *
   * Built inside it, they were rebuilt whenever ANY dependency changed —
   * including `turns`, which changes on every streamed chunk. `PearlPanel`
   * keys its Escape listener on `closePanel`, so it was tearing down and
   * re-adding a keyboard listener dozens of times per reply.
   */
  const openPanel = useCallback(() => setPanelOpen(true), [])
  const closePanel = useCallback(() => setPanelOpen(false), [])

  const value = useMemo<PearlChat>(
    () => ({
      turns,
      draft,
      busy,
      status,
      error,
      setDraft,
      ask,
      accept,
      clear,
      panelOpen,
      openPanel,
      closePanel,
    }),
    [turns, draft, busy, status, error, ask, accept, clear, panelOpen, openPanel, closePanel],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

type StreamEvent =
  | { type: 'text' | 'status' | 'error'; text: string }
  | { type: 'view'; view: View }
  | { type: 'done' }

/**
 * Read the server-sent-event stream from our own Function.
 *
 * Anything unparseable is skipped rather than thrown — a single malformed
 * frame should not lose the whole reply.
 */
async function readStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (e: Exclude<StreamEvent, { type: 'done' }>) => void,
) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  /**
   * Every frame goes through here, including the last one.
   *
   * The loop used to `break` on `done` and drop whatever was still in the
   * buffer — so a stream whose final event was not followed by a blank line
   * lost that event. In practice that is the closing chunk of text or the
   * `view` frame: the "Show me" button silently absent from the last reply.
   */
  const drain = (chunk: string) => {
    buffer += chunk
    // Frames are separated by a blank line. Keep the trailing partial frame.
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''
    return frames
  }

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) {
        // Whatever is left is a whole frame with no blank line after it.
        for (const frame of drain(decoder.decode())) if (handle(frame, onEvent)) return
        if (buffer.trim() !== '') handle(buffer, onEvent)
        return
      }
      for (const frame of drain(decoder.decode(value, { stream: true }))) {
        if (handle(frame, onEvent)) return
      }
    }
  } finally {
    // Releasing the lock matters on the `done` event path, which returns with
    // the body undrained.
    try {
      reader.releaseLock()
    } catch {
      // Already released, or already errored. Nothing to do either way.
    }
  }
}

/** One frame. Returns true when the stream said it was finished. */
function handle(frame: string, onEvent: (e: Exclude<StreamEvent, { type: 'done' }>) => void): boolean {
  for (const line of frame.split('\n')) {
    if (!line.startsWith('data:')) continue
    const payload = line.slice(5).trim()
    if (payload === '') continue
    try {
      const evt = JSON.parse(payload) as StreamEvent
      if (evt.type === 'done') return true
      // A frame missing the field its type promises is a frame we cannot act on
      // — skip it rather than render "undefined" at her.
      if (evt.type === 'view') {
        // The SAME predicate storage uses. They used to be two checks with a
        // comment claiming they were one, so a view with no label rendered live
        // as a card with an empty title and a working button, and lost the
        // button entirely after a refresh.
        if (usableView(evt.view)) onEvent(evt)
      } else if (typeof evt.text === 'string') {
        onEvent(evt)
      }
    } catch {
      // Ignore a frame we cannot parse.
    }
  }
  return false
}
